import React, { useState } from 'react';
import {
  SparklesIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

import { API_URL } from '../constants/urls';

interface ResearchWidgetProps {
  segmentId: string;
  organizationId: string;
  canEdit: boolean;
  hasGenAIAccess: boolean;
  planType?: string;
  isAdmin?: boolean;
  onResearchComplete?: (noteId: string) => void;
}

const ResearchWidget: React.FC<ResearchWidgetProps> = ({
  segmentId,
  organizationId,
  canEdit,
  hasGenAIAccess,
  planType = 'free',
  isAdmin = false,
  onResearchComplete,
}) => {
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchInstructions, setResearchInstructions] = useState('');
  const [researchModel, setResearchModel] = useState('sonar');
  const [recencyFilter, setRecencyFilter] = useState('month');
  const [showSettings, setShowSettings] = useState(false);

  const handleStartResearch = async () => {
    if (!segmentId || !organizationId) return;

    setResearchLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/segments/${segmentId}/research?organization_id=${organizationId}`,
        {
          research_type: 'MARKET_RESEARCH',
          provider_preference: 'perplexity',
          depth: 'standard',
          custom_instructions: researchInstructions,
          model_preference: researchModel,
          search_recency_filter: recencyFilter,
        },
      );

      if (response.data.success) {
        toast.success('Research completed successfully!');

        // Clear the instructions after successful research
        setResearchInstructions('');

        // Notify parent component if callback provided
        if (onResearchComplete && response.data.note_id) {
          onResearchComplete(response.data.note_id);
        }
      }
    } catch (error: any) {
      console.error('Research failed:', error);
      const message =
        error.response?.data?.detail || 'Research failed. Please try again.';
      toast.error(message);
    } finally {
      setResearchLoading(false);
    }
  };

  if (!canEdit) {
    return null;
  }

  if (!hasGenAIAccess) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-blue-600" />
            AI Research Assistant
          </h3>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-blue-800 font-medium">
                AI Research requires{' '}
                {planType === 'free' ? 'a paid plan' : 'plan upgrade'}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {isAdmin ? (
                  <a
                    href="/organization?tab=billing"
                    className="underline hover:text-blue-900"
                  >
                    Upgrade your plan
                  </a>
                ) : (
                  'Contact your organization admin'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-blue-600" />
          AI Research Assistant
        </h3>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Research Settings"
        >
          <Cog6ToothIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Main Controls */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Additional Research Instructions
          </label>
          <textarea
            placeholder="Focus on specific aspects, competitors, or regions..."
            value={researchInstructions}
            onChange={(e) => setResearchInstructions(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            rows={3}
          />
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Frame
            </label>
            <select
              value={recencyFilter}
              onChange={(e) => setRecencyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="day">Past Day</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="quarter">Past Quarter</option>
              <option value="year">Past Year</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleStartResearch}
              disabled={researchLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
            >
              {researchLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Researching...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4" />
                  Run Research
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Settings */}
      {showSettings && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">
              Advanced Settings
            </h4>
            <button
              onClick={() => setShowSettings(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              <ChevronUpIcon className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Research Model
            </label>
            <select
              value={researchModel}
              onChange={(e) => setResearchModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="sonar">Sonar (Fast, Standard)</option>
              <option value="sonar_pro">Sonar Pro (Enhanced)</option>
              <option value="sonar_deep_research">
                Deep Research (Most Comprehensive)
              </option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Choose the depth and quality of research
            </p>
          </div>
        </div>
      )}

      {/* Status Message */}
      {researchLoading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            Conducting market research... This may take up to a minute.
          </p>
        </div>
      )}
    </div>
  );
};

export default ResearchWidget;
